import dayjs from "dayjs";
import {
	BucketSetting,
	DateRangePreset,
	Grain,
	ReportDateRange,
} from "../../../models/ReportBuilder";

export const DATE_RANGE_PRESETS: { value: DateRangePreset; label: string }[] = [
	{ value: "today", label: "Idag" },
	{ value: "yesterday", label: "Igår" },
	{ value: "this_week", label: "Denna vecka" },
	{ value: "last_week", label: "Förra veckan" },
	{ value: "this_month", label: "Denna månad" },
	{ value: "last_month", label: "Förra månaden" },
	{ value: "this_quarter", label: "Detta kvartal" },
	{ value: "last_quarter", label: "Förra kvartalet" },
	{ value: "this_year", label: "I år" },
	{ value: "last_year", label: "Förra året" },
	{ value: "year_to_date", label: "Hittills i år" },
	{ value: "last_7_days", label: "Senaste 7 dagarna" },
	{ value: "last_30_days", label: "Senaste 30 dagarna" },
	{ value: "last_90_days", label: "Senaste 90 dagarna" },
	{ value: "last_12_months", label: "Senaste 12 månaderna" },
	{ value: "custom", label: "Eget intervall" },
];

/** The chips shown in the report header. */
export const QUICK_PRESETS: DateRangePreset[] = [
	"today",
	"this_week",
	"this_month",
	"this_quarter",
	"this_year",
	"last_30_days",
];

const FORMAT = "YYYY-MM-DD";

/** Inclusive from/to dates for a preset, today included. */
export function resolveDateRange(
	range: ReportDateRange,
	now = dayjs()
): { from: string; to: string } {
	const today = now.startOf("day");
	// ISO week: Monday first, without the dayjs isoWeek plugin.
	const monday = today.subtract((today.day() + 6) % 7, "day");
	const quarterStart = today.startOf("month").subtract(today.month() % 3, "month");
	switch (range.preset) {
		case "today":
			return { from: today.format(FORMAT), to: today.format(FORMAT) };
		case "yesterday":
			return {
				from: today.subtract(1, "day").format(FORMAT),
				to: today.subtract(1, "day").format(FORMAT),
			};
		case "this_quarter":
			return {
				from: quarterStart.format(FORMAT),
				to: quarterStart.add(3, "month").subtract(1, "day").format(FORMAT),
			};
		case "last_quarter":
			return {
				from: quarterStart.subtract(3, "month").format(FORMAT),
				to: quarterStart.subtract(1, "day").format(FORMAT),
			};
		case "year_to_date":
			return { from: today.startOf("year").format(FORMAT), to: today.format(FORMAT) };
		case "last_7_days":
			return { from: today.subtract(6, "day").format(FORMAT), to: today.format(FORMAT) };
		case "last_90_days":
			return { from: today.subtract(89, "day").format(FORMAT), to: today.format(FORMAT) };
		case "this_week":
			return { from: monday.format(FORMAT), to: monday.add(6, "day").format(FORMAT) };
		case "last_week":
			return {
				from: monday.subtract(7, "day").format(FORMAT),
				to: monday.subtract(1, "day").format(FORMAT),
			};
		case "this_month":
			return {
				from: today.startOf("month").format(FORMAT),
				to: today.endOf("month").format(FORMAT),
			};
		case "last_month": {
			const m = today.subtract(1, "month");
			return { from: m.startOf("month").format(FORMAT), to: m.endOf("month").format(FORMAT) };
		}
		case "this_year":
			return { from: today.startOf("year").format(FORMAT), to: today.endOf("year").format(FORMAT) };
		case "last_year": {
			const y = today.subtract(1, "year");
			return { from: y.startOf("year").format(FORMAT), to: y.endOf("year").format(FORMAT) };
		}
		case "last_30_days":
			return { from: today.subtract(29, "day").format(FORMAT), to: today.format(FORMAT) };
		case "last_12_months":
			return {
				from: today.subtract(11, "month").startOf("month").format(FORMAT),
				to: today.endOf("month").format(FORMAT),
			};
		case "custom":
		default: {
			const from = range.from || today.startOf("year").format(FORMAT);
			const to = range.to || today.format(FORMAT);
			return from <= to ? { from, to } : { from: to, to: from };
		}
	}
}

export const presetLabel = (preset: DateRangePreset): string =>
	DATE_RANGE_PRESETS.find((p) => p.value === preset)?.label ?? preset;

export function describeDateRange(range: ReportDateRange): string {
	const preset = DATE_RANGE_PRESETS.find((p) => p.value === range.preset);
	const { from, to } = resolveDateRange(range);
	return range.preset === "custom"
		? `${from} – ${to}`
		: `${preset?.label ?? range.preset} (${from} – ${to})`;
}

const MONTHS_SHORT = [
	"jan",
	"feb",
	"mar",
	"apr",
	"maj",
	"jun",
	"jul",
	"aug",
	"sep",
	"okt",
	"nov",
	"dec",
];

const shortDate = (value: string, withYear: boolean): string => {
	const d = dayjs(value);
	return `${d.date()} ${MONTHS_SHORT[d.month()]}${withYear ? ` ${d.year()}` : ""}`;
};

/** "1 jan – 31 dec 2026", or "1 jan 2025 – 31 dec 2026" across years. */
export function formatRangeShort(range: { from: string; to: string }): string {
	const sameYear = range.from.substring(0, 4) === range.to.substring(0, 4);
	return `${shortDate(range.from, !sameYear)} – ${shortDate(range.to, true)}`;
}

/** Whole days in an inclusive range. */
export const rangeDays = (range: { from: string; to: string }): number =>
	dayjs(range.to).diff(dayjs(range.from), "day") + 1;

/**
 * The same range one step earlier or later: a calendar preset moves by its
 * own unit and becomes a custom range; anything else moves by its length.
 */
export function stepRange(range: ReportDateRange, direction: -1 | 1): ReportDateRange {
	const { from, to } = resolveDateRange(range);
	const f = dayjs(from);
	const t = dayjs(to);
	const unit =
		f.date() === 1 && t.isSame(t.endOf("month"), "day") ? Math.round(t.diff(f, "month", true)) : 0;
	if (unit > 0) {
		const nf = f.add(direction * unit, "month");
		return {
			preset: "custom",
			from: nf.format(FORMAT),
			to: nf.add(unit, "month").subtract(1, "day").format(FORMAT),
		};
	}
	const days = rangeDays({ from, to });
	return {
		preset: "custom",
		from: f.add(direction * days, "day").format(FORMAT),
		to: t.add(direction * days, "day").format(FORMAT),
	};
}

/** Mirrors the backend: day up to a month, week up to half a year, month up to three years, then quarters. */
export function autoGrain(days: number): Grain {
	if (days <= 31) return "day";
	if (days <= 26 * 7) return "week";
	if (days <= 3 * 366) return "month";
	return "quarter";
}

export const effectiveGrain = (
	bucket: BucketSetting | undefined,
	range: { from: string; to: string }
): Grain => (bucket && bucket !== "auto" ? bucket : autoGrain(rangeDays(range)));

/** Which grain a bucket label is in, from its shape; undefined when it is not a bucket label. */
export function grainOfLabel(value: unknown): Grain | undefined {
	if (typeof value !== "string") return undefined;
	if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return "day";
	if (/^\d{4}-W\d{2}$/.test(value)) return "week";
	if (/^\d{4}-\d{2}$/.test(value)) return "month";
	if (/^\d{4}-Q[1-4]$/.test(value)) return "quarter";
	if (/^\d{4}$/.test(value)) return "year";
	return undefined;
}

/** "8 sep", "v. 37", "sep 2026", "Q3 2026", "2026" for the axis and tooltips. */
export function formatBucketLabel(value: unknown, withYear = false): string {
	const grain = grainOfLabel(value);
	const text = String(value ?? "");
	switch (grain) {
		case "day":
			return shortDate(text, withYear);
		case "week":
			return `v. ${Number(text.substring(6))}${withYear ? ` ${text.substring(0, 4)}` : ""}`;
		case "month":
			return `${MONTHS_SHORT[Number(text.substring(5, 7)) - 1]} ${text.substring(0, 4)}`;
		case "quarter":
			return `${text.substring(5)} ${text.substring(0, 4)}`;
		default:
			return text;
	}
}

const isoWeekLabel = (d: dayjs.Dayjs): string => {
	// Thursday of the same ISO week decides the week-year; week number from that year's first Thursday.
	const thursday = d.add(3 - ((d.day() + 6) % 7), "day");
	const firstThursday = dayjs(`${thursday.year()}-01-04`);
	const week1Monday = firstThursday.subtract((firstThursday.day() + 6) % 7, "day");
	const week = Math.round(thursday.diff(week1Monday, "day") / 7) + 1;
	return `${thursday.year()}-W${String(week).padStart(2, "0")}`;
};

/** Every bucket label between from and to, in order, so a series can show empty periods as zero. */
export function bucketLabels(range: { from: string; to: string }, grain: Grain): string[] {
	const out: string[] = [];
	const end = dayjs(range.to);
	const start = dayjs(range.from);
	// Walk from the start of the first bucket so the last bucket is reached even when `to` is early in it.
	let d =
		grain === "day"
			? start
			: grain === "week"
			? start.subtract((start.day() + 6) % 7, "day")
			: start.startOf("month");
	while (!d.isAfter(end, "day")) {
		out.push(
			grain === "day"
				? d.format(FORMAT)
				: grain === "week"
				? isoWeekLabel(d)
				: grain === "month"
				? d.format("YYYY-MM")
				: grain === "quarter"
				? `${d.year()}-Q${Math.floor(d.month() / 3) + 1}`
				: String(d.year())
		);
		d =
			grain === "day"
				? d.add(1, "day")
				: grain === "week"
				? d.add(7, "day")
				: grain === "month"
				? d.add(1, "month")
				: grain === "quarter"
				? d.add(3, "month")
				: d.add(1, "year");
	}
	return out;
}

/**
 * Inserts zero rows for buckets that have no row, when every row is one
 * bucket (no second dimension). Rows are left alone otherwise.
 */
export function fillGaps<T extends Record<string, unknown>>(
	rows: T[],
	xKey: string,
	yKeys: string[],
	grain: Grain,
	range: { from: string; to: string }
): T[] {
	if (!rows.length || rows.some((r) => grainOfLabel(r[xKey]) !== grain)) return rows;
	const byLabel = new Map(rows.map((r) => [String(r[xKey]), r]));
	if (byLabel.size !== rows.length) return rows;
	const labels = bucketLabels(range, grain);
	if (labels.length > 1000 || !labels.length) return rows;
	const template = rows[0];
	return labels.map((label) => {
		const found = byLabel.get(label);
		if (found) return found;
		const empty: Record<string, unknown> = {};
		Object.keys(template).forEach(
			(k) => (empty[k] = yKeys.includes(k) ? 0 : k === xKey ? label : null)
		);
		return empty as T;
	});
}
